/**
 * Packaging quantity math — one rule for every level (primary / secondary / tertiary):
 *
 *     netAfterLoss      = qtyForPacking - loss          (loss is ALWAYS a typed entry)
 *     packagingQuantity = floor(netAfterLoss / packSize)  (whole packs only)
 *     packedQuantity    = packagingQuantity * packSize
 *     remainder         = netAfterLoss - packedQuantity   (returned to the source lot)
 *
 * Unit-agnostic: weight for primary, pack counts for secondary/tertiary. The API is authoritative
 * and re-verifies any client-computed value.
 */
import { Decimal, toDecimal, ZERO, type DecimalInput } from "./decimal";
import { DomainError } from "./errors";

export type PackagingLevel = "primary" | "secondary" | "tertiary";

export const PACKAGING_TOLERANCE = new Decimal("0.01");

export interface PackagingResult {
  netAfterLoss: Decimal;
  packagingQuantity: number;
  packedQuantity: Decimal;
  remainder: Decimal;
}

export function computePackaging(
  qtyForPacking: DecimalInput,
  loss: DecimalInput,
  packSize: DecimalInput
): PackagingResult {
  const size = toDecimal(packSize);
  if (size.lte(0))
    throw new DomainError(
      "pack_size_invalid",
      "Packaging pack size must be greater than zero."
    );
  let netAfterLoss = toDecimal(qtyForPacking).minus(toDecimal(loss));
  if (netAfterLoss.lt(0)) netAfterLoss = ZERO;
  const packagingQuantity = netAfterLoss
    .div(size)
    .toDecimalPlaces(0, Decimal.ROUND_DOWN)
    .toNumber();
  const packedQuantity = size.times(packagingQuantity);
  return {
    netAfterLoss,
    packagingQuantity,
    packedQuantity,
    remainder: netAfterLoss.minus(packedQuantity),
  };
}

/** Cross-field guards enforced before any mutation. Throws DomainError naming the field. */
export function validatePackagingInput(
  level: PackagingLevel,
  qtyForPacking: DecimalInput,
  loss: DecimalInput,
  available: DecimalInput
): void {
  const qty = toDecimal(qtyForPacking);
  const lossDec = toDecimal(loss);
  if (qty.lte(0))
    throw new DomainError(
      "qty_not_positive",
      "Quantity for packing must be greater than zero.",
      `${level}_qty_for_packing`
    );
  if (qty.gt(toDecimal(available)))
    throw new DomainError(
      "qty_exceeds_available",
      "Quantity for packing cannot exceed available inventory.",
      `${level}_qty_for_packing`
    );
  if (lossDec.lt(0))
    throw new DomainError(
      "loss_negative",
      "Loss cannot be negative.",
      `${level}_loss`
    );
  if (lossDec.gt(qty))
    throw new DomainError(
      "loss_exceeds_qty",
      "Loss cannot exceed the quantity for packing.",
      `${level}_loss`
    );
}

/**
 * The two distinct `packagingQuantity == 0` cases. Returns true for a FULL LOSS (allowed: no child
 * lot, source still debited, loss recorded); throws when there is material but not enough for one pack.
 */
export function guardPackagingQuantity(
  level: PackagingLevel,
  packagingQuantity: number,
  netAfterLoss: DecimalInput
): boolean {
  if (packagingQuantity > 0) return false;
  if (toDecimal(netAfterLoss).gt(0)) {
    throw new DomainError(
      "insufficient_for_one_pack",
      "Quantity is insufficient to form one complete package.",
      `${level}_packaging_quantity`
    );
  }
  return true;
}

/** True when a client-supplied value drifts from the computed one beyond tolerance (blank = no drift). */
export function packagingQuantityDrifts(
  computed: number,
  clientValue: DecimalInput
): boolean {
  if (clientValue === null || clientValue === undefined || clientValue === "")
    return false;
  return new Decimal(computed)
    .minus(toDecimal(clientValue))
    .abs()
    .gt(PACKAGING_TOLERANCE);
}
